import * as gateway from 'sfx-web-contract';
import { AlkLogger } from '@alk/core';
import { BLOTTER_CHUNK_SIZE, compact, pluckAndFilter, pluckTyped } from '../../../helpers';
import { COMMUNICATION_SERVICE, ICommunicationService } from '../../communication';
import { createOrdersRequest, ordersTransformator, validateSpotOrder } from './blotter-adapter.service.utils';
import { finalize, map, takeUntil, tap } from 'rxjs/operators';
import { IBlotterAdapter } from './blotter-adapter.interface';
import { IBlotterTransaction } from '../../../interfaces';
import { inject, injectable } from 'inversify';
import { ISessionLifecycle, SESSION_LIFECYCLE_SERVICE } from '../../session-lifecycle';
import { Observable } from 'rxjs';
import { referenceEqualerCurried } from '../../../helpers/object-utils';

@injectable()
export class BlotterAdapterService implements IBlotterAdapter {

    @inject(COMMUNICATION_SERVICE) private readonly communicationService: ICommunicationService;

    @inject(SESSION_LIFECYCLE_SERVICE) private readonly sessionLifeCycle: ISessionLifecycle;

    public ordersObservable = (clientId: number): Observable<IBlotterTransaction> => {

        const subToken = this.createRequest(clientId);

        return this.communicationService.dataObservable(gateway.MessageTypes.Orders)
            .pipe(
                takeUntil(this.sessionLifeCycle.destroyObservable()),
                pluckAndFilter('communicationToken', referenceEqualerCurried(subToken)),
                finalize(() => this.deleteRequest(subToken)),
                pluckTyped('payload'),
                compact(),
                map(this.decode),
                map(this.validateResponse),
                tap(data => AlkLogger.debug(`[BLOTTER_ADAPTER_SERVICE#feed] received: ${ JSON.stringify(data) }`)),
                map(ordersTransformator),
            );
    };

    private decode(data: Uint8Array): gateway.IOrdersResponse {
        try {
            return gateway.OrdersResponse.decode(data);
        } catch (error) {
            AlkLogger.error(`[BLOTTER_ADAPTER_SERVICE] Decoding error ${ data } not decode to SpotOrdersResponse message`);
            throw error;
        }
    }

    private validateResponse(ordersChangeSet: gateway.IOrdersResponse): gateway.IOrdersResponse {

        const validateOrderHandler = (order) => {

            const validationResult = validateSpotOrder(order);

            if (Boolean(validationResult)){
                throw validationResult;
            } else {
                return order;
            }
        };

        ordersChangeSet.NewOrders?.forEach(validateOrderHandler);

        return ordersChangeSet;
    }

    private readonly createRequest = (clientId: number): number =>
        this.communicationService
            .create(
                createOrdersRequest(clientId, BLOTTER_CHUNK_SIZE),
                gateway.MessageTypes.Orders,
            );

    private readonly deleteRequest = (communicationToken: number): void => {

        AlkLogger.debug(`[BLOTTER_ADAPTER_SERVICE#deleteRequest] is about to delete ${ ''
        }subscription with id: ${ communicationToken }`);

        this.communicationService.delete(communicationToken);
    };
}
